import { CommonModule } from "@angular/common";
import { createCustomElement, NgElement, WithProperties } from '@angular/elements';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Injector, Input, Output } from "@angular/core";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { SettingLookupOption } from "@tylertech/tyler-api-types";

function isDefined(): boolean
{
    return customElements.get(ProfileCardComponent.customElementSelector) !== undefined;
}

export function defineProfileCardComponent(injector: Injector): void
{
    if (isDefined())
    {
        return;
    }

    const profileCardComponent = createCustomElement(ProfileCardComponent, { injector });
    customElements.define(ProfileCardComponent.customElementSelector, profileCardComponent);
}

export function createProfileCardElement(inputs: ProfileCardInputs): NgElement & WithProperties<ProfileCardComponent>
{
    const customElement = document.createElement(ProfileCardComponent.customElementSelector) as NgElement & WithProperties<ProfileCardComponent>;
    customElement.items = inputs.items;
    customElement.version = inputs.version;
    customElement.passwordExpiring = inputs.passwordExpiring;
    customElement.notifications = inputs.notifications;
    customElement.showWebLauncherDownload = inputs.showWebLauncherDownload;

    return customElement;
}

export const enum ProfileCardOutput
{
    ChangePassword = "changePassword",
    ViewNotifications = "viewNotifications",
    DownloadWebLauncher = "downloadWebLauncher",
}

type ProfileCardOutputs = "changePassword" | "viewNotifications" | "downloadWebLauncher";

type ProfileCardIgnoredProperties =
    | ProfileCardOutputs
    | "onChangePasswordClick" | "onViewClick" | "onDownloadWebLauncherClick";
type ProfileCardInputs = Omit<
    Pick<ProfileCardComponent, keyof ProfileCardComponent>,
    ProfileCardIgnoredProperties>;

@Component({
    selector: "profile-card",
    templateUrl: "./profileCard.component.html",
    styleUrls: [
        "./profileCard.component.scss"
    ],
    imports: [
        CommonModule,
        FlexLayoutModule,
        FlexModule,
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
// TODO - Refactor component to accept `Array<Label | Command>` instead of explicitly implementing each item.
export class ProfileCardComponent
{
    /**
     * The selector for registering the angular element as.
     * NOTE: this should be different than the `@Component` selector to avoid any confusion.
     */
    static readonly customElementSelector = "erp-pro-profile-card";

    @Input() items: SettingLookupOption[];
    @Input() version: string;
    @Input() passwordExpiring: boolean;
    @Input() notifications: number;
    @Input() showWebLauncherDownload: boolean;

    @Output() readonly changePassword = new EventEmitter<void>();
    @Output() readonly viewNotifications = new EventEmitter<void>();
    @Output() readonly downloadWebLauncher = new EventEmitter<void>();

    onChangePasswordClick(): void
    {
        this.changePassword.emit();
    }

    onViewClick(): void
    {
        this.viewNotifications.emit();
    }

    onDownloadWebLauncherClick(): void
    {
        this.downloadWebLauncher.emit();
    }
}
